import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { ShareModal } from '../components/ShareModal';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import { getDocument, listDocumentVersions, archiveDocument, type DocumentRow } from '../lib/db';
import { getShareUrl, getSignedUrl } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { DOC_TEMPLATES } from '../data/docTemplates';
import { LoadingIndicator } from '../components/LoadingIndicator';

export function DocumentView() {
  const { docId } = useParams<{ docId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const [doc, setDoc] = useState<DocumentRow | null>(null);
  const [versions, setVersions] = useState<DocumentRow[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareNotice, setShareNotice] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareJustCreated, setShareJustCreated] = useState(false);
  const [senderName, setSenderName] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!docId) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const d = await getDocument(docId);
        if (!d) { setError('Document not found.'); setLoading(false); return; }
        setDoc(d);
        if (d.template_id) {
          const vs = await listDocumentVersions(d.org_id, d.template_id, d.site_id);
          setVersions(vs);
        }
        if (d.file_path) {
          const url = await getSignedUrl('documents', d.file_path, 3600);
          setPdfUrl(url);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [docId]);

  async function copyShareLink() {
    if (!doc?.file_path) return;
    try {
      const url = await getShareUrl('documents', doc.file_path, 30 * 24 * 60 * 60);
      if (navigator.clipboard) await navigator.clipboard.writeText(url);
      setShareNotice('Share link copied — valid for 30 days.');
      setTimeout(() => setShareNotice(null), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function openShareModal() {
    setShareJustCreated(false);
    setShareOpen(true);
  }

  // Auto-open the share modal when redirected here after creating a document.
  useEffect(() => {
    if (searchParams.get('share') === '1' && doc) {
      setShareJustCreated(true);
      setShareOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('share');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, doc]);

  // Stash the user's name for the "Sent by" line in the email body.
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      setSenderName(name);
    });
  }, []);

  async function archive() {
    if (!doc) return;
    if (!(await confirm({
      title: 'Archive this document?',
      body: 'It will no longer appear in the library, but the file stays in storage.',
      confirmLabel: 'Archive',
      danger: true,
    }))) return;
    try {
      await archiveDocument(doc.id);
      nav('/documents');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (loading) return <AppShell title="Loading…" back="/documents"><LoadingIndicator /></AppShell>;
  if (!doc) return <AppShell title="Document" back="/documents"><div className="banner banner-error">{error ?? 'Not found.'}</div></AppShell>;

  const template = DOC_TEMPLATES.find((t) => t.id === doc.template_id);
  const supersedeHref = doc.template_id
    ? (doc.site_id
        ? `/documents/site/${doc.site_id}/template/${doc.template_id}?supersede=${doc.id}`
        : `/documents/org/template/${doc.template_id}?supersede=${doc.id}`)
    : null;

  return (
    <AppShell title={doc.title} back="/documents">
      {error && <div className="banner banner-error">{error}</div>}
      {shareNotice && <div className="banner banner-success">{shareNotice}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}><strong>{doc.title}</strong></p>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
          Version <strong>{doc.version}</strong>
          {' · '}Status <strong>{doc.status}</strong>
          {' · '}{doc.scope === 'org' ? 'Organisation-wide' : 'Site-specific'}
          {doc.expires_at && ' · expires ' + new Date(doc.expires_at).toLocaleDateString('en-GB')}
        </p>
        {template && (
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>{template.description}</p>
        )}
      </div>

      <div className="btn-stack">
        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
            Open PDF
          </a>
        )}
        <button type="button" className="btn btn-ghost" onClick={copyShareLink}>
          Copy share link (30 days)
        </button>
        <button type="button" className="btn btn-ghost" onClick={openShareModal}>
          📨 Email this document
        </button>
        {supersedeHref && (
          <Link to={supersedeHref} className="btn btn-ghost">Issue new version</Link>
        )}
        <button type="button" className="btn btn-ghost btn-danger" onClick={archive}>
          Archive
        </button>
      </div>

      {versions.length > 1 && (
        <>
          <h3 className="section-title" style={{ marginTop: 22 }}>Version history</h3>
          <div className="doc-list">
            {versions.map((v) => (
              <div key={v.id} className="card" style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>v{v.version}</strong>
                    {' · '}<span className={`status-pill status-${v.is_current ? 'ok' : 'pending'}`}>{v.status}</span>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                      Issued {v.issued_at ? new Date(v.issued_at).toLocaleDateString('en-GB') : '—'}
                    </p>
                  </div>
                  {v.id !== doc.id && (
                    <Link to={`/documents/${v.id}`} className="btn btn-ghost btn-sm">View</Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        docTitle={doc.title}
        docType="Document"
        orgName={org?.org_name ?? undefined}
        senderName={senderName || undefined}
        introCopy={
          shareJustCreated
            ? 'Document saved. Send a copy to anyone who needs it — main contractor, CDM coordinator, client.'
            : undefined
        }
        getShareUrl={() => {
          if (!doc.file_path) throw new Error('No file attached to this document.');
          return getShareUrl('documents', doc.file_path, 30 * 24 * 60 * 60);
        }}
      />
    </AppShell>
  );
}
